import React, { useEffect, useRef, useState } from 'react';
interface Tab {
  id: string;
  label: string;
  mobileLabel?: string; // short label for mobile (optional)
  icon?: React.ReactNode;
  content?: React.ReactNode;
}
interface TabsProps {
  tabs: Tab[];
  defaultTab?: string;
  activeTab?: string;
  onChange?: (tabId: string) => void;
  children?: React.ReactNode;
}
export function Tabs({
  tabs,
  defaultTab,
  activeTab: controlledActiveTab,
  onChange,
  children
}: TabsProps) {
  const [internalActiveTab, setInternalActiveTab] = useState(defaultTab || tabs[0]?.id || '');
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;
  const setActiveTab = (tabId: string) => {
    if (onChange) onChange(tabId);else setInternalActiveTab(tabId);
  };

  // Check scroll state
  const checkScroll = () => {
    const el = navRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, {
      passive: true
    });
    window.addEventListener('resize', checkScroll);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [tabs]);

  // Scroll active tab into view on change
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }, [activeTab]);
  const activeTabContent = tabs.find(t => t.id === activeTab)?.content;
  return <div data-id="element-4909">
      {/* Tab bar wrapper — relative so we can overlay fades */}
      <div className="relative border-b border-gray-200 dark:border-gray-700" data-id="element-4910">
        {/* Left fade */}
        {canScrollLeft && <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-8 z-10
            bg-gradient-to-r from-white dark:from-gray-900 to-transparent" data-id="element-4911" />}
        {/* Right fade — always visible when overflowing */}
        {canScrollRight && <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 z-10
            bg-gradient-to-l from-white dark:from-gray-900 to-transparent" data-id="element-4912" />}

        <nav ref={navRef} className="flex overflow-x-auto" aria-label="Tabs" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-4913">
          {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} ref={isActive ? activeTabRef : undefined} onClick={() => setActiveTab(tab.id)} className={`
                  group relative flex items-center gap-1.5 px-3 sm:px-4 py-3
                  text-xs sm:text-sm font-medium whitespace-nowrap
                  border-b-2 -mb-px transition-all duration-150 flex-shrink-0
                  ${isActive ? 'border-sky-500 text-sky-600 dark:text-sky-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'}
                `} data-id="element-4914">
                {/* Icon — always visible */}
                {tab.icon && <span className={`flex-shrink-0 transition-colors ${isActive ? 'text-sky-500' : 'text-gray-400 group-hover:text-gray-500'}`} data-id="element-4915">
                    {tab.icon}
                  </span>}

                {/* On mobile: show mobileLabel if provided, else hide text if icon exists */}
                {tab.mobileLabel ? <>
                    {/* short label on mobile */}
                    <span className="sm:hidden" data-id="element-4916">{tab.mobileLabel}</span>
                    {/* full label on desktop */}
                    <span className="hidden sm:inline" data-id="element-4917">{tab.label}</span>
                  </> : (/* no mobileLabel: show full label always (but truncate) */
            <span className="max-w-[80px] sm:max-w-none truncate sm:overflow-visible" data-id="element-4918">
                    {tab.label}
                  </span>)}

                {/* Active dot indicator for mobile icon-only style */}
                {isActive && tab.mobileLabel === '' && <span className="sm:hidden absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-sky-500" data-id="element-4919" />}
              </button>;
        })}
        </nav>
      </div>

      <div className="pt-6" data-id="element-4920">{activeTabContent || children}</div>
    </div>;
}
interface TabPanelProps {
  id: string;
  activeTab: string;
  children: React.ReactNode;
}
export function TabPanel({
  id,
  activeTab,
  children
}: TabPanelProps) {
  if (id !== activeTab) return null;
  return <div data-id="element-4921">{children}</div>;
}